import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ViewBundleOffer from 'components/OfferList/OfferListTiles/SearchOfferTiles/ViewBundleOffer'
import HotelSearchTileAlternativeDatesBanner from 'components/SearchKit/SearchTiles/accommodations/HotelSearchTileAlternativeDatesBanner'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, PRODUCT_TYPE_LIMITED_TIME_SPECIAL } from 'constants/offer'
import useOffer from 'hooks/Offers/useOffer'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import { nonNullable } from 'lib/array/arrayUtils'
import { getPackageUniqueKey, isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { logNewRelic } from 'services/newRelic'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import ExperimentalHotelSearchTileFeatures from './ExperimentalHotelSearchTileFeatures'
import HotelSearchTileAction from './HotelSearchTileAction'
import HotelSearchTileEarnablePoints from './HotelSearchTileEarnablePoints'
import HotelSearchTileFeatures from './HotelSearchTileFeatures'
import HotelSearchTileHighlightBanner from './HotelSearchTileHighlightBanner'
import HotelSearchTileLabels from './HotelSearchTileLabels'
import useMetaSearchDistance from './useMetaSearchDistance'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'
import HotelSearchTileUnavailabilityBanner from './HotelSearchTileUnavailabilityBanner'
import HotelSearchTileCharterFareClassBanner from './HotelSearchTileCharterFareClassBanner'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SearchTileComparisonCheckbox from 'components/SearchKit/SearchTiles/shared/SearchTileComparisonCheckbox'
import { diffInDays } from 'lib/datetime/dateUtils'
import { generalLog } from 'marketing/api'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import LoyaltyStayUpgradeBenefitMessageBanner from 'loyalty/components/LoyaltyStayUpgradeBenefitMessageBanner'
import useOfferRating from 'hooks/Offers/useOfferRating'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import RichTooltip from 'LuxKit/RichTooltip'
import { CHARTER_PAGE_SLUG, MALDIVES_BUNDLE_LABEL, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import StructureLabel from 'LuxKit/Label/StructureLabel'

const LUX_EXCLUSIVE_SESSION_FLAG_KEY = 'le_lux_exclusive_tooltip_shown'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  filters?: App.OfferListFilters
  showPerNightPricing?: boolean
  showLimitedTimeLuxExclusiveTooltip?: boolean
  storeSearchTile?: boolean
  showDescription?: boolean
  position?: number
}

function HotelSearchTile({ offer, filters, showPerNightPricing = false, showLimitedTimeLuxExclusiveTooltip = false, storeSearchTile = false, showDescription = false, position }: Props) {
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters?.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const isSpecificSearch = !!(checkIn && checkOut && filters?.rooms)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)

  const [, fetchingOffer] = useOffer<App.Offer>(offer.id, {
    requireSummaryOnly: !isSpecificSearch,
  })

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration

  const hasFiltersSet = !!(checkIn && checkOut && filters?.rooms)

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage && fullPath.includes('metasearch')) {
        const priceTotal = queryParams.priceTotal || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceTotal) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          logNewRelic('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      // use search pricing package id by default

      if (!pkgId || !duration || !bestMaybeAvailableRate?.rate?.roomRateId) {
        return offer.lowestPricePackage
      }

      const pkg = offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, bestMaybeAvailableRate.rate.roomRateId))
      return pkg ?? offer.lowestPricePackage
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const correctBestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage) {
        const priceShown = queryParams.priceShown || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceShown) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          logNewRelic('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      // use search pricing package id by default
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      const roomRateId = metaData?.pricing?.lowestPriceRoomRateId ?? bestMaybeAvailableRate?.rate?.roomRateId

      if (!pkgId || !duration || !roomRateId) {
        return
      }

      return offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, roomRateId))
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const productType = (isLPCTacticalEnabled() && offer.hasTactical) ? PRODUCT_TYPE_LIMITED_TIME_SPECIAL : offer.productType

  const locations = useMemo<Array<string>>(() => nonNullable([offer.locationHeading, offer.locationSubheading]), [offer])
  const mapUrl = useMapSearchOfferUrl(storeSearchTile, offer, filters)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const showProductLabel = isSpoofed || productType !== OFFER_TYPE_ALWAYS_ON

  // Maldives Direct charter bundles should use calendar-based availability (multi-room allocation),
  // since offerBestPrices can incorrectly report unavailable for the selected occupancy.
  const { charterBundle: isMaldivesDirectCharterBundleForSoldOut } = useMaldivesDirectBundleContext()
  const { pricingByOfferId: pricingByOfferIdForSoldOut, isLoading: isMaldivesPricingLoadingForSoldOut } = useMaldivesOffersPricingContext()
  const contextPricingForSoldOut = pricingByOfferIdForSoldOut[offer.id]

  const isSoldOutFromSearch = shouldUseRealSearchPrices
    ? !metaData?.available
    : (hasFiltersSet && bestMaybeAvailableRate !== undefined && !bestMaybeAvailableRate.available)

  const isSoldOut = (isMaldivesDirectCharterBundleForSoldOut &&
    !isMaldivesPricingLoadingForSoldOut &&
    contextPricingForSoldOut) ?
      !contextPricingForSoldOut.hasAvailability :
    isSoldOutFromSearch

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        availability: false,
        price: undefined,
        roomRateId: undefined,
        memberPrice: undefined,
        duration: undefined,
        extendedAvailability: metaData?.extendedAvailability,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const showSpoofHighlight = useMemo<boolean>(() => {
    return isSpoofed && !scheduleIsCurrent(offer.visibilitySchedule)
  }, [isSpoofed, offer.visibilitySchedule])

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights

  const showHighlight = useMemo(() => {
    if (showSpoofHighlight) return true
    if (showDescription) return false

    if (!filters?.propertyId || filters?.searchNearby) return false
    return offer.property?.id === filters.propertyId.split(':')[1]
  }, [filters, offer, showSpoofHighlight, showDescription])
  const showSpecificRoomRateAndRoomType = filters?.roomRateId && filters?.roomTypeId
  const bestAvailableRate = bestMaybeAvailableRate?.available && !showSpecificRoomRateAndRoomType ? bestMaybeAvailableRate.rate : undefined
  const hidePrice = useHideAnytimePrices(offer.type, metaData?.hidePricing)
  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const rating = useOfferRating(offer, metaData, fullPath.includes('metasearch'))
  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const isUpgradeAvailableForSelectedDates = isAnytimeSearch ? true : (metaData?.packagesWithAvailableUpgrade.length ?? 0) > 0

  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  const [showLuxExclusiveTooltip, setShowLuxExclusiveTooltip] = useState<boolean>(false)
  const onDismissLuxExclusiveTooltip = useCallback(() => {
    setShowLuxExclusiveTooltip(false)
  }, [])

  // Only show per session and only when the user is not logged in
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isLoggedIn = !!memberId

  useEffect(() => {
    if (!showLimitedTimeLuxExclusiveTooltip) return
    if (isLoggedIn) return
    if (typeof window === 'undefined') return
    try {
      if (sessionStorage.getItem(LUX_EXCLUSIVE_SESSION_FLAG_KEY) === 'true') return
    } catch {}
    setShowLuxExclusiveTooltip(true)
    try { sessionStorage.setItem(LUX_EXCLUSIVE_SESSION_FLAG_KEY, 'true') } catch {}
  }, [showLimitedTimeLuxExclusiveTooltip, isLoggedIn])

  const isMaldivesCharter = useAppSelector((state: App.State) => getIsSpecificCampaignPage(state, CHARTER_PAGE_SLUG))

  // Check if this is a charter bundle offer by checking campaignTags
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])

  const productTypeLabelNode = useMemo<React.ReactNode>(() => {
    // Show "Hotel & Flight bundle" label for charter bundle offers
    if (isCharterBundleOffer) {
      return <StructureLabel variant="maldives-charter">Hotel & Flight bundle</StructureLabel>
    }
    // Hide in bundle mode
    if (isMaldivesCharter) {
      return null
    }
    if (!(showLimitedTimeLuxExclusiveTooltip || showProductLabel)) {
      return null
    }
    if (showLimitedTimeLuxExclusiveTooltip) {
      return (
        <RichTooltip
          description="Luxury Escapes packages you can't book anywhere else!"
          placement="top-start"
          open={showLuxExclusiveTooltip}
          dismissible
          onDismiss={onDismissLuxExclusiveTooltip}
        >
          <ProductTypeLabel productType={productType} />
        </RichTooltip>
      )
    }
    return <ProductTypeLabel productType={productType} />
  }, [isCharterBundleOffer, isMaldivesCharter, showLimitedTimeLuxExclusiveTooltip, showProductLabel, showLuxExclusiveTooltip, onDismissLuxExclusiveTooltip, productType])

  const handleSelectDatesClick = useCallback(() => {
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })

  return <SearchTile
    offer={offer}
    state={showHighlight ? 'highlighted' : 'default'}
    action={!isSoldOut && <HotelSearchTileAction offer={offer} offerMetaData={metaData} onSelectDatesClick={handleSelectDatesClick} />}
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {showHighlight && <HotelSearchTileHighlightBanner offer={offer} offerMetaData={metaData} filters={filters} />}
      <HotelSearchTileCharterFareClassBanner offerId={offer.id} />
      {offerSiteTakeover?.searchPage?.tileBanner}
    </>}
    bookmarkAction={storeSearchTile ? undefined : <BookmarkButton offer={offer} withLabel="always" />}
    cancellationPolicyLabel={(!noGoogleAffiliations || isFlashHotelOffer(offer)) && !!cancellationPolicyType && <TileCancellationPolicy
      cancellationPolicyType={cancellationPolicyType}
      checkInDate={filters?.checkIn}
      timezoneOffset={offer.property.timezoneOffset}
      offerType={offer.type}
    />}
    bottomExtension={<>
      {config.ENABLE_BUNDLE_AND_SAVE && metaData?.bundleOfferId && <ViewBundleOffer
        offer={offer}
        filters={filters}
        offerMetaData={metaData}
        offerLinkIncludesFilters
      />}
      {metaData?.extendedAvailability && <HotelSearchTileAlternativeDatesBanner
        checkIn={metaData.extendedAvailability.checkIn}
        checkOut={metaData.extendedAvailability.checkOut}
      />}
      {isSoldOut && !metaData?.extendedAvailability &&
        <HotelSearchTileUnavailabilityBanner
          offer={offer}
          unavailableReason={isMaldivesDirectCharterBundleForSoldOut ? contextPricingForSoldOut?.unavailableReason ?? metaData?.unavailableReason : metaData?.unavailableReason}
          filters={filters}
        />}
    </>}
    topExtension={
      (hasUpgradablePackages && hasRoomUpgradesAvailable) &&
      <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} upgradeNotAvailableForSelectedDates={!isUpgradeAvailableForSelectedDates} />
    }
    features={<>
      {!noGoogleAffiliations || isFlashHotelOffer(offer) ?
          <HotelSearchTileFeatures offer={offer} offerMetaData={metaData} filters={filters} bestPackage={bestPackage} doNotOpenInclusionsModal={storeSearchTile} /> :
          <ExperimentalHotelSearchTileFeatures offer={offer} offerMetaData={metaData} filters={filters} bestPackage={correctBestPackage} />}
    </>}
    images={offer.images}
    labels={
      <HotelSearchTileLabels
        offer={offer}
        filters={filters}
        bestPackage={bestPackage}
        showUrgencyTags={!isSoldOut}
      />
    }
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={isCharterBundleOffer || isMaldivesCharter ? undefined : mapUrl} />}
    pricePoint={!isSoldOut && <HotelSearchTilePriceStack
      offer={offer}
      filters={filters}
      bestPackage={bestPackage}
      duration={duration ?? 1}
      bestRate={bestAvailableRate}
      hidePrice={hidePrice}
      showPerNightPricing={showPerNightPricing}
      hideIncludesTaxesAndFees={storeSearchTile}
    />}
    productLabel={<LabelGroup>
      {productTypeLabelNode}
      <OfferPartnershipLabel offer={offer} />
      {isMaldivesCharter && !isCharterBundleOffer && <Label kind="primary" variant="maldives-charter">{MALDIVES_BUNDLE_LABEL}</Label>}
      {filters?.showDistanceWithMetaOfferId && metaSearchDistanceToMainOffer && <PromotionLabel variant="default">{metaSearchDistanceToMainOffer}</PromotionLabel>}
    </LabelGroup>}
    rating={rating}
    title={`${offer.property.name}${isCharterBundleOffer && !offer.property.name.toLocaleLowerCase().includes(' & flights') ? ' & Flights' : ''}`}
    meta={!storeSearchTile && <HotelSearchTileEarnablePoints
      offer={offer}
      bestPackage={bestPackage}
      bestAvailableRate={bestAvailableRate}
      filters={filters}
      metaData={metaData}
    />}
    comparisonCheckbox={<SearchTileComparisonCheckbox offerType={offer.type} offerId={offer.id} name={offer.property.name} filters={filters} />}
  />
}

export default HotelSearchTile
